import { useState } from "react";
import { useParams } from "react-router-dom";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import NoDocuments from "Assets/Img/NoDocuments";
import { downloadDocumentBlob } from "services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import { useToast } from "Components/Common/Toast/ToastContext";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import Button from "Components/Common/Button/Button";

interface Uploader {
  firstName: string;
  lastName: string;
  companyName?: string;
}

interface DocumentItem {
  id: string;
  label: string;
  category: string;
  value: { key: string; fileName: string };
  addOns: { date: string | null; status: string };
  uploader: Uploader;
  createdAt: string;
}

interface Props {
  documents: Record<string, DocumentItem[]> | null;
  loading: boolean;
}

const getIconLabel = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(ext)) return "XLS";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "IMG";
  return "FILE";
};

const formatGroupKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DocumentSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div className="doc-item" key={i}>
          <Skeleton width={40} height={40} borderRadius={6} />
          <div className="doc-info">
            <Skeleton width={260} height={14} />
            <Skeleton width={180} height={11} style={{ marginTop: 6 }} />
          </div>
          <Skeleton width={52} height={30} borderRadius={6} />
        </div>
      ))}
    </>
  );
}

export default function DocumentsSection({ documents, loading }: Props) {
  const { id: projectId } = useParams<{ id: string }>();
  const showToast = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (doc: DocumentItem) => {
    setDownloadingId(doc.id);
    const blob = await downloadDocumentBlob(projectId!, doc.id);
    if (blob)
      triggerBlobDownload(blob, doc.label ?? doc.value?.fileName ?? "document");
    else showToast("Download failed. Please try again.");
    setDownloadingId(null);
  };

  if (loading) {
    return (
      <div>
        <h1 className="sectionTitle">Documents &amp; Files</h1>
        {[1, 2].map((i) => (
          <div className="doc-category" key={i}>
            <Skeleton width={180} height={13} style={{ marginBottom: 12 }} />
            <DocumentSkeleton />
          </div>
        ))}
      </div>
    );
  }

  const entries = documents ? Object.entries(documents) : [];
  const totalCount = entries.reduce((sum, [, docs]) => sum + docs.length, 0);

  if (!totalCount) {
    return (
      <div>
        <h1 className="sectionTitle mb-4">Documents &amp; Files</h1>
        <EmptyState
          icon={<NoDocuments />}
          title="No Documents Yet"
          subtitle="Project documents will appear here once they are uploaded and published."
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="sectionTitle">Documents &amp; Files</h1>
      <div className="section-sub">
        All project documents · {totalCount} files
      </div>

      {entries.map(([groupKey, docs]) => (
        <div className="doc-category" key={groupKey}>
          <div className="doc-cat-title">{formatGroupKey(groupKey)}</div>
          {docs.map((doc) => {
            const uploaderName = [
              doc.uploader?.firstName,
              doc.uploader?.lastName,
            ]
              .filter(Boolean)
              .join(" ");
            const date = doc.addOns?.date
              ? new Date(doc.addOns.date).toLocaleDateString()
              : new Date(doc.createdAt).toLocaleDateString();
            const meta = [uploaderName, doc.uploader?.companyName, date]
              .filter(Boolean)
              .join(" · ");

            return (
              <div className="doc-item" key={doc.id}>
                <div className="doc-icon">
                  {getIconLabel(doc.value.fileName)}
                </div>
                <div className="doc-info">
                  <div className="doc-name">{doc.label}</div>
                  {meta && <div className="doc-meta">{meta}</div>}
                </div>
                <Button
                  className="doc-btn"
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                >
                  {downloadingId === doc.id ? (
                    <>
                      <span className="btn-spinner" /> Downloading…
                    </>
                  ) : (
                    "Download"
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
